import api from './api';

export const clientsService = {
  async getClients(page = 1, limit = 25, filters = {}) {
    const params = { page, limit };
    if (filters.search)   params.search   = filters.search;
    if (filters.status)   params.status   = filters.status;
    if (filters.country)  params.country  = filters.country;
    if (filters.office)   params.office   = filters.office;
    if (filters.agent?.length) params.agent = filters.agent;
    if (filters.language) params.language = filters.language;
    if (filters.department) params.department = filters.department;
    if (filters.desk?.length) params.desk = filters.desk;
    if (filters.team?.length) params.team = filters.team;
    if (filters.ftd)      params.ftd      = filters.ftd;
    if (filters.affiliate) params.affiliate = filters.affiliate;
    if (filters.salesStatus) params.sales_status = filters.salesStatus;
    if (filters.retStatus)   params.ret_status   = filters.retStatus;
    if (filters.refSearch)   params.ref_search   = filters.refSearch;
    if (filters.balanceMin !== undefined && filters.balanceMin !== '') params.balance_min = filters.balanceMin;
    if (filters.balanceMax !== undefined && filters.balanceMax !== '') params.balance_max = filters.balanceMax;
    if (filters.regFrom)  params.reg_from = filters.regFrom;
    if (filters.regTo)    params.reg_to   = filters.regTo;
    if (filters.modFrom)  params.mod_from = filters.modFrom;
    if (filters.modTo)    params.mod_to   = filters.modTo;
    if (filters.active !== undefined && filters.active !== '') params.active = filters.active;
    if (filters.needsUpdate !== undefined && filters.needsUpdate !== '') params.needs_update = filters.needsUpdate;
    if (filters.sortBy) params.sort_by = filters.sortBy;
    if (filters.sortDir) params.sort_dir = filters.sortDir;
    // indexes:null → repeated keys (agent=a&agent=b) as FastAPI List[str] expects
    const response = await api.get('/clients', { params, paramsSerializer: { indexes: null } });
    return response.data;
  },

  async getClient(id) {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  async createClient(clientData) {
    const response = await api.post('/clients', clientData);
    return response.data;
  },

  async updateClient(id, clientData) {
    const response = await api.put(`/clients/${id}`, clientData);
    return response.data;
  },

  async deleteClient(id) {
    const response = await api.delete(`/clients/${id}`);
    return response.data;
  },

  async bulkDelete(clientIds) {
    const response = await api.post('/clients/bulk/delete', { client_ids: clientIds });
    return response.data;
  },

  async getStats() {
    const response = await api.get('/clients/stats');
    return response.data;
  },

  async getDuplicates(criterion) {
    const response = await api.get('/clients/duplicates', {
      params: criterion ? { criterion } : undefined,
    });
    return response.data;
  },

  async checkDuplicates(contactDetails) {
    const response = await api.post('/clients/duplicate-check', contactDetails);
    return response.data;
  },

  async getMergePreview(clientIds) {
    const response = await api.post('/clients/merge-preview', { client_ids: clientIds });
    return response.data;
  },

  async mergeClients(primaryId, secondaryIds, fieldSources = {}) {
    const response = await api.post('/clients/merge', {
      primary_id: primaryId,
      secondary_ids: secondaryIds,
      field_sources: fieldSources,
    });
    return response.data;
  },

  async previewImport(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/clients/import/preview', formData);
    return response.data;
  },

  async importClients(file, mapping, preset) {
    const formData = new FormData();
    formData.append('file', file);
    if (mapping) formData.append('mapping', JSON.stringify(mapping));
    if (preset)  formData.append('preset',  JSON.stringify(preset));
    const response = await api.post('/clients/import', formData);
    return response.data;
  },

  // Bulk actions
  async bulkAssign(clientIds, agent) {
    const response = await api.post('/clients/bulk/assign', { client_ids: clientIds, value: agent });
    return response.data;
  },

  async bulkBlock(clientIds) {
    const response = await api.post('/clients/bulk/block', { client_ids: clientIds });
    return response.data;
  },

  async bulkUnblock(clientIds) {
    const response = await api.post('/clients/bulk/unblock', { client_ids: clientIds });
    return response.data;
  },

  async bulkChangeStatus(clientIds, statuses, department) {
    const response = await api.post('/clients/bulk/status', { client_ids: clientIds, statuses, department });
    return response.data;
  },

  async bulkChangeOffice(clientIds, office) {
    const response = await api.post('/clients/bulk/office', { client_ids: clientIds, value: office });
    return response.data;
  },

  async bulkChangeCountry(clientIds, country) {
    const response = await api.post('/clients/bulk/country', { client_ids: clientIds, value: country });
    return response.data;
  },

  async bulkChangeLanguage(clientIds, language) {
    const response = await api.post('/clients/bulk/language', { client_ids: clientIds, value: language });
    return response.data;
  },

  // Options for dropdowns
  async getFilterOptions(includeOfficeStructure = true) {
    const requests = {
      agents: () => clientsService.getFilterAgents(),
      offices: () => clientsService.getOffices(),
      countries: () => clientsService.getCountries(),
      statuses: () => clientsService.getStatuses(),
      languages: () => clientsService.getLanguages(),
      desks: () => includeOfficeStructure ? clientsService.getDesks() : [],
      teams: () => includeOfficeStructure ? clientsService.getTeams() : [],
      affiliates: () => clientsService.getAffiliateOptions(),
    };
    const keys = Object.keys(requests);
    const results = await Promise.allSettled(keys.map(key => Promise.resolve().then(requests[key])));
    const options = {};
    const failed = [];
    results.forEach((result, index) => {
      const key = keys[index];
      options[key] = result.status === 'fulfilled' ? result.value : [];
      if (result.status === 'rejected') failed.push(key);
    });
    return { options, failed };
  },

  async getAgents() {
    const response = await api.get('/options/agents');
    return response.data.agents;
  },

  async getFilterAgents() {
    const response = await api.get('/clients/filter-agents');
    return response.data.users;
  },

  async getOffices() {
    const response = await api.get('/options/offices');
    return response.data.offices;
  },

  async getCountries() {
    const response = await api.get('/options/countries');
    return response.data.countries;
  },

  async getLanguages() {
    const response = await api.get('/options/languages');
    return response.data.languages;
  },

  async getAffiliateOptions() {
    const response = await api.get('/options/affiliates');
    return response.data.affiliates;
  },

  async getDesks() {
    const response = await api.get('/desks');
    return response.data;
  },

  async getTeams() {
    const response = await api.get('/teams');
    return response.data;
  },

  async getStatuses(entityType = 'all') {
    const response = await api.get('/options/statuses', { params: { entity_type: entityType } });
    return response.data.statuses;
  }
};
